/**
 * 写真マスタ（自前ホスト・クレジット付き）
 *
 * 収録の原則：
 *   1. **その場所・その打ち上げの実写だけを載せる。** イメージ写真で埋めない。
 *      個別の観覧スポット（恵美之江展望公園・宮原ロケット見学場など）は自由ライセンスの
 *      実写が存在しないため、**あえて写真を置かない**（別の射場の写真を流用すると誤誘導になる）。
 *   2. すべて `public/assets/photos/` から自前配信（外部CDNへの実行時通信を作らない）。
 *   3. ライセンスは Wikimedia Commons API の extmetadata で実測確認（2026-07-28）。
 *      CC BY / CC BY-SA は著作者表示が条件のため、画面上にクレジットを必ず出す。
 */

export interface Photo {
  /** /assets/photos/ 配下のファイル名 */
  file: string;
  /** 代替テキスト（何が写っているか） */
  alt: string;
  /** 画面に出す説明。撮影時期など「今と違う点」があればここで明示する */
  caption: string;
  /** 著作者表示 */
  author: string;
  /** ライセンス名 */
  license: string;
  /** ライセンス本文 */
  licenseUrl: string;
  /** 出典（ファイルページ） */
  sourceUrl: string;
  width: number;
  height: number;
}

/** トップのヒーロー：打ち上げを「観覧している」実写＝このサイトの主題そのもの */
export const HERO_PHOTO: Photo = {
  file: 'hero-viewing.jpg',
  alt: '種子島宇宙センターから打ち上がるH3ロケットを、手前の見学者たちが見上げている',
  caption: '種子島宇宙センターからのH3ロケット打ち上げ（2025年2月・みちびき/QZSS-HP）を見守る人たち',
  author: 'U.S. Space Force',
  license: 'パブリックドメイン',
  licenseUrl: 'https://commons.wikimedia.org/wiki/Template:PD-USGov',
  sourceUrl:
    'https://commons.wikimedia.org/wiki/File:U_S_,_Japan_launch_the_QZSS-HP_in_first_bilateral_national_security_space_cooperation_effort_(8853513).jpg',
  width: 800,
  height: 1000,
};

/** 射場ごとの実写（射場ガイドの見出し用）。キーは LaunchSite.id */
export const SITE_PHOTOS: Record<string, Photo> = {
  tanegashima: {
    file: 'site-tanegashima.jpg',
    alt: '海に面した種子島宇宙センター大崎射場。左手に大型ロケット組立棟、中央に射点の避雷塔が並ぶ',
    caption: '種子島宇宙センター 大崎射場。打ち上げは海に向かって行われる',
    author: 'ウニウニ',
    license: 'CC BY-SA 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/deed.ja',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Osaki_Range.jpg',
    width: 900,
    height: 599,
  },
  uchinoura: {
    file: 'site-uchinoura.jpg',
    alt: '海に面した丘の上に建つ内之浦宇宙空間観測所。右手に大型パラボラアンテナ、左手前に発射装置が見える',
    caption: '内之浦宇宙空間観測所（星ヶ丘方面から）。段丘の上に射点と大型アンテナが並ぶ',
    author: 'Ippukucho',
    license: 'CC BY 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by/3.0/deed.ja',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:UchinouraSpaceCenterFromHoshigaoka.JPG',
    width: 900,
    height: 675,
  },
  spaceport_kii: {
    file: 'site-spaceport_kii.jpg',
    alt: 'スペースポート紀伊の建設中の空中写真。射点・ロケット組立棟・総合指令棟の位置が注記されている',
    caption:
      'スペースポート紀伊の空中写真（2019年10月・建設中／射点などの注記は出典元による）。完成後の現在の姿とは異なる',
    author: '国土地理院（Wikimedia Commons 投稿者による注記あり）',
    license: '出典明示で利用可（国土地理院 空中写真）',
    licenseUrl: 'https://www.gsi.go.jp/kikakuchousei/kikakuchousei40182.html',
    sourceUrl:
      'https://commons.wikimedia.org/wiki/File:Aerial_photo_of_Space_Port_Kii_under_construction._Photographed_on_October_5,_2019.jpg',
    width: 800,
    height: 640,
  },
  hokkaido_spaceport: {
    file: 'site-hokkaido_spaceport.jpg',
    alt: '大樹町多目的航空公園の駐車場と管制塔、「多目的航空公園 スカイスポーツ広場」の木製看板',
    caption: '大樹町多目的航空公園（無料中継が行われる実績のある場所）。射点そのものではない',
    author: 'Misaso-',
    license: 'CC BY-SA 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/deed.ja',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Taiki_Aerospace_Research_Field.jpg',
    width: 900,
    height: 506,
  },
};

export function getSitePhoto(siteId: string): Photo | undefined {
  return SITE_PHOTOS[siteId];
}
